import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import Button from 'yoda-core-components/lib/components/Button';
import Icon from 'yoda-core-components/lib/components/Icon/Icon';
import CouponFill from 'yoda-core-components/lib/assets/svg/CouponFill.svg';
import classNames from 'classnames/bind';
import MessageBox from 'yoda-core-components/lib/components/MessageBox/MessageBox';
import Cookies from 'yoda-core-components/lib/helpers/Cookies/Cookies';
import {
    CURRENCY_US,
    ANALYTICS_ACTIONS_FIND_COUPON,
} from 'yoda-checkout-components/lib/common/Constants';
import {
    AMR_CART_APPLY_BEST_COUPON,
    AMR_CART_APPLY_BETTER_COUPON,
} from 'yoda-checkout-components/lib/common/AmrConstants';
import * as checkoutAnalyticsAction from 'yoda-checkout-components/lib/actions/CheckoutAnalyticsAction';
import * as slidePanelActions from 'yoda-checkout-components/lib/actions/SlidePanelAction';
import * as coCouponActions from 'yoda-checkout-components/lib/actions/COCouponAction';
import * as serviceErrorActions from 'yoda-checkout-components/lib/actions/ServiceErrorHandlerAction';
import currencyFormatter from 'currency-formatter';
import { dt } from 'yoda-core-components/lib/helpers/Utils/GetTailwindToken';
import * as styles from './SpecialOffers.css';
import BestCouponModal from '../BestCouponModal/BestCouponModal';

const cx = classNames.bind(styles);

export class SpecialOffers extends Component {
    static propTypes = {
        applyCOAdjustment: PropTypes.func,
        bestCoupon: PropTypes.arrayOf(PropTypes.object),
        isBetterCouponAvailable: PropTypes.bool,
        errorInfo: PropTypes.arrayOf(PropTypes.object),
        actions: PropTypes.objectOf(PropTypes.func).isRequired,
        display: PropTypes.bool,
        showGlobalLoader: PropTypes.bool,
        isSmallerScreen: PropTypes.bool,
        enableNewOrderSummaryRedesign: PropTypes.bool,
        idSuffix: PropTypes.string,
    };

    static defaultProps = {
        applyCOAdjustment: () => {},
        bestCoupon: {},
        isBetterCouponAvailable: false,
        errorInfo: {},
        display: false,
        showGlobalLoader: false,
        isSmallerScreen: false,
        enableNewOrderSummaryRedesign: true,
        idSuffix: '',
    };

    componentDidMount() {
        /* istanbul ignore next */
        this.rootEl = (!__SERVER__ && document && document.getElementsByTagName('body')[0]) || null;
        setTimeout(() => {
            Cookies.remove('showCouponMessage');
        }, 2000);
    }

    onApply = () => {
        const { bestCoupon } = this.props;
        const couponCode = bestCoupon.code;
        this.props.applyCOAdjustment(
            {
                code: couponCode,
                serialNumber: null,
                // App-exclusive coupons carry appOnly; forward it so the apply-adjustment
                // request sends the appOnly header (matches the coupon slider apply path).
                ...(bestCoupon.appOnly && { appOnly: bestCoupon.appOnly }),
            },
            true
        );
    };

    /* istanbul ignore next */
    toggleModal = () => {
        const { bestCoupon } = this.props;
        const bestCouponDetails = [bestCoupon];
        this.props.actions.toggleCouponDetailsSlider(true);
        this.props.actions.openCouponDetailSlider(bestCouponDetails);
        this.openCouponPanel();
    };

    getDisplayClasses = () => {
        const {
            display,
            showGlobalLoader,
            isSmallerScreen,
            enableNewOrderSummaryRedesign,
        } = this.props;
        const migratedCss = dt([
            'pb-2',
            'md:p-0',
            'overflow-hidden',
            !isSmallerScreen && 'lg:block',
            !isSmallerScreen && 'lg:mt-4',
            'mdOnly:w-1/2',
        ]);
        /* istanbul ignore if */
        if (display) {
            return `${cx('offers-animate', migratedCss)} ${dt([
                'lg:mb-1',
                'mx-auto',
                'my-0',
                'md:inline-block',
                showGlobalLoader && 'bg-opacity-50',
                !showGlobalLoader && 'opacity-100',
                'max-h-56',
                enableNewOrderSummaryRedesign ? 'lg:mb-4' : 'lg:mb-1',
            ])}`;
        }
        return `${cx('offers-animate', 'splOfferContainer', migratedCss)}
            ${dt(['mb-2', 'opacity-0', 'max-h-0', 'invisible', 'mx-auto', 'my-0'])}
        `;
    };

    showCouponAmount = () => {
        const {
            bestCoupon: { totalDiscount: couponDiscount, code, appOnly },
        } = this.props;
        return (
            <>
                Save {currencyFormatter.format(couponDiscount, { code: CURRENCY_US, precision: 2 })}{' '}
                <div
                    className={`${cx('withCode')} ${dt([
                        'font-open-sans-semibold-italic',
                        'text-small',
                    ])}`}
                >
                    with{appOnly ? '' : ' code'}{' '}
                    <Button
                        buttonType="Link"
                        size="Sm"
                        onClick={this.toggleModal}
                        className={dt(['font-open-sans-semibold', '!text-brand-red'])}
                    >
                        {/* App-exclusive coupons must NOT expose the coupon code; show
                            "App-exclusive coupon" as the CTA instead of the code value. */}
                        {appOnly ? 'App-exclusive coupon' : code}
                    </Button>
                </div>
            </>
        );
    };

    /* istanbul ignore next */
    openCouponPanel = (e) => {
        e?.preventDefault();
        this.props.actions.clearServiceErrorMessage();
        this.props.actions.showCouponsPanel(true);
        this.props.actions.getCOCouponsAction(true);
        this.props.actions.clickEventAnalyticsAction(ANALYTICS_ACTIONS_FIND_COUPON);
    };

    render() {
        const { isBetterCouponAvailable, errorInfo, bestCoupon } = this.props;
        const isModalOpen = get(this.state, 'isModalOpen', false);
        const couponLabel = isBetterCouponAvailable ? 'Better Coupon Found' : 'Best Coupon';
        const amrId = `${
            isBetterCouponAvailable ? AMR_CART_APPLY_BETTER_COUPON : AMR_CART_APPLY_BEST_COUPON
        }${this.props.idSuffix}`;
        const classes = this.getDisplayClasses();
        const bestCouponDetails = [bestCoupon];
        const priceContainerCss = dt([
            'flex',
            'flex-row',
            'justify-between',
            'h-24',
            'py-2.5',
            'sm:px-2.5',
            'md:px-6',
        ]);
        const clipIconWrapperCss = dt(['h-8', 'pr-2']);
        const generalInfoLinkCss = dt(['block', 'text-black', 'font-open-sans-regular']);
        const finalPriceCss = dt([
            'block',
            'leading-8',
            'text-brand-red',
            'p-0',
            'text-xLg',
            'font-open-sans-bold',
        ]);
        return (
            <>
                <div className={classes} data-automation-id="at-fpac-container">
                    <div
                        id={amrId}
                        className={`${cx('priceContainer', priceContainerCss)} ${dt([
                            'rounded-small',
                            'border-dashed',
                            'text-gray-15',
                            'bg-white',
                            'mb-1',
                            'lg:mb-5',
                            'p-1',
                            'border-2',
                            'lg:mt-0',
                            'rounded-lg',
                        ])}`}
                    >
                        <div className={dt(['flex', 'items-center', 'pb-1'])}>
                            <div className={cx('clipIconWrapper', clipIconWrapperCss)}>
                                <Icon
                                    automationId="at-clip"
                                    iconType="svg"
                                    width="32px"
                                    height="32px"
                                    viewBox="0 0 40 40"
                                    name="coupon-fill"
                                    className={`${cx('icon-clip')} ${dt(['text-black'])}`}
                                >
                                    <CouponFill />
                                </Icon>
                            </div>
                            <div className={`${cx('couponInfoWrapper')} ${dt(['text-left'])}`}>
                                <Button
                                    buttonType="Link"
                                    className={cx('generalInfoLink', generalInfoLinkCss)}
                                    automationId="couponDetails"
                                    size="Md"
                                    onClick={this.openCouponPanel}
                                >
                                    {couponLabel}
                                </Button>
                                <span
                                    className={cx('finalPrice', finalPriceCss)}
                                    data-automation-id="at-final-price-after-coupon"
                                >
                                    {this.showCouponAmount()}
                                </span>
                            </div>
                        </div>
                        <div className={dt(['flex', 'items-center'])}>
                            <Button
                                automationId={
                                    isBetterCouponAvailable
                                        ? 'alternate-FPAC-on-cart'
                                        : 'FPAC-apply-on-cart'
                                }
                                buttonId={
                                    isBetterCouponAvailable
                                        ? 'alternate-FPAC-on-cart'
                                        : 'FPAC-apply-on-cart'
                                }
                                buttonType="Secondary"
                                className={cx('specialOfferApplyBtn')}
                                size="Mini"
                                onClick={this.onApply}
                            >
                                Apply
                            </Button>
                        </div>
                    </div>
                    {errorInfo.errorMessage && (
                        <div
                            className={`${cx('coupon-error-container')} ${dt([
                                'w-full',
                                'float-left',
                            ])}`}
                        >
                            <MessageBox
                                type="error"
                                level="section"
                                title={errorInfo.errorMessage}
                                hasClose={false}
                                theme={dt(['sm:mt-5', 'lg:mt-1', 'mx-0', 'sm:mb-1', 'lg:mb-5'])}
                                automationId="at-fpac-error"
                            />
                        </div>
                    )}
                </div>
                <BestCouponModal
                    isModalOpen={isModalOpen}
                    bestCouponDetails={bestCouponDetails}
                    closeModal={this.toggleModal}
                />
            </>
        );
    }
}

const mapStateToProps = (state) => {
    const enableNewOrderSummaryRedesign = get(
        state,
        'context.featureFlags.enableNewOrderSummaryRedesign',
        true
    );
    return {
        enableNewOrderSummaryRedesign,
    };
};

const mapDispatchToProps = (dispatch) => ({
    actions: bindActionCreators(
        {
            ...slidePanelActions,
            ...coCouponActions,
            ...checkoutAnalyticsAction,
            ...serviceErrorActions,
        },
        dispatch
    ),
});

export default connect(mapStateToProps, mapDispatchToProps)(SpecialOffers);
